"use client";

import { BookOpen, FileText, Send, TrendingUp, Users, Siren, Heart, Target, Sliders, Activity, Telescope, Crosshair, Search, Star, Globe, Brain, Radio, Cpu, BarChart3, Shield, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { capabilitiesOverview as data } from "@/lib/data/platform-capabilities";

const iconMap: Record<string, LucideIcon> = {
  BookOpen, FileText, Send, TrendingUp, Users, Siren, Heart, Target, Sliders, Activity,
  Telescope, Crosshair, Search, Star, Globe, Brain, Radio, Cpu, BarChart3, Shield,
};

export function CapabilitiesOverview() {
  return (
    <Section id="capabilities-overview" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">Capabilities Overview</span>
            <h2 className="mt-3">The Twenty Core Capabilities of Perception X2</h2>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] w-12">#</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Capability</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Domain</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden lg:table-cell">Key Metric</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    const Icon = iconMap[row.icon] ?? BookOpen;
                    return (
                      <tr
                        key={row.number}
                        className="border-b border-[rgb(var(--border)/0.08)] hover:bg-[rgb(var(--border)/0.04)] transition-colors group"
                      >
                        <td className="px-4 py-3 align-top">
                          <span
                            className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                            style={{
                              background: `color-mix(in oklch, var(--color-${row.accent}) 18%, transparent)`,
                              boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${row.accent}) 35%, transparent)`,
                            }}
                          >
                            <Icon className="h-3.5 w-3.5" style={{ color: `var(--color-${row.accent})` }} />
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-baseline gap-2">
                            <span
                              className="eyebrow-tiny font-bold"
                              style={{ color: `var(--color-${row.accent})` }}
                            >
                              {row.number}
                            </span>
                            <span className="font-semibold">{row.name}</span>
                          </div>
                          <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed md:hidden">
                            {row.domain} · {row.metric}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))] hidden md:table-cell">
                          {row.domain}
                        </td>
                        <td className="px-4 py-3 align-top hidden lg:table-cell">
                          <span
                            className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                            style={{
                              background: `color-mix(in oklch, var(--color-${row.accent}) 18%, transparent)`,
                              color: `var(--color-${row.accent})`,
                            }}
                          >
                            {row.metric}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        <Reveal delay={300}>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.rows.slice(0, 4).map((row) => {
              const Icon = iconMap[row.icon] ?? BookOpen;
              return (
                <div
                  key={`detail-${row.number}`}
                  className="glass rounded-2xl p-5"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${row.accent}) 12%, transparent)`,
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="grid place-items-center h-9 w-9 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, var(--color-${row.accent}) 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${row.accent}) 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: `var(--color-${row.accent})` }} />
                    </span>
                    <span
                      className="eyebrow-tiny font-bold"
                      style={{ color: `var(--color-${row.accent})` }}
                    >
                      {row.number}
                    </span>
                  </div>
                  <h3 className="text-base">{row.name}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{row.description}</p>
                </div>
              );
            })}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
